import { useState, useEffect } from 'react';
import { Image } from '../../types';
import { downloadBlobDataByURLs, FILE_TYPES, FetchedBlobImage } from '../../utils/blobUtils';
import { extractTimeStampLatLongValuesFromURL } from '../utils/imageUtils';

/** Same shape as StopImagesDialog ImageList ({ key, blobData }) */
export interface BlobImageItem {
  key: string | number;
  blobData: string;
}

type CacheEntry = {
  blobImages: BlobImageItem[];
  fingerprint: string;
};

// Mirrors StopImagesDialog proImages / stopImages cache keyed by tab
const blobImagesCache: Record<string, CacheEntry> = {};

export const clearBlobImagesCache = () => {
  Object.keys(blobImagesCache).forEach(key => delete blobImagesCache[key]);
};

export const getHttpImages = (images: Image[]) =>
  images.filter(img => img.url?.startsWith('http'));

const getImagesFingerprint = (images: Image[]) =>
  images.map(img => `${img.id}:${img.url}`).join('|');

// Mirrors getMappedImageDataWithTimestamp in StopImagesDialog/getBlobImages.js
export const getMappedImageDataWithTimestamp = (images: Image[]) =>
  images.map((img, index) => {
    const { timestamp } = extractTimeStampLatLongValuesFromURL(img.url);
    return {
      key: timestamp ?? img.id ?? index,
      url: img.url,
    };
  });

export const toBlobImageItems = (fetchedImages: FetchedBlobImage[]): BlobImageItem[] =>
  fetchedImages.map(image => ({
    key: image.key,
    blobData: `${FILE_TYPES.IMAGE}${image.blobData}`,
  }));

const getCachedEntry = (cacheKey: string, httpImages: Image[]) => {
  const cached = blobImagesCache[cacheKey];
  if (!cached) return null;
  return cached.fingerprint === getImagesFingerprint(httpImages) ? cached : null;
};

export const getCachedBlobImages = (cacheKey: string, httpImages: Image[]) =>
  getCachedEntry(cacheKey, httpImages)?.blobImages ?? null;

export const setCachedBlobImages = (
  cacheKey: string,
  httpImages: Image[],
  blobImages: BlobImageItem[]
) => {
  blobImagesCache[cacheKey] = {
    blobImages,
    fingerprint: getImagesFingerprint(httpImages),
  };
};

// Mirrors getImagesFromBlobStorage in StopImagesDialog/getBlobImages.js
export const useBlobImages = (images: Image[], cacheKey: string) => {
  const httpImages = getHttpImages(images);
  const cachedEntry = getCachedEntry(cacheKey, httpImages);

  const [blobImages, setBlobImages] = useState<BlobImageItem[]>(
    () => cachedEntry?.blobImages ?? []
  );
  const [fetchError, setFetchError] = useState(false);
  const [isLoading, setIsLoading] = useState(
    () => !cachedEntry && httpImages.length > 0
  );

  useEffect(() => {
    if (images.length === 0) {
      setBlobImages([]);
      setFetchError(false);
      setIsLoading(false);
      return;
    }

    if (!httpImages.length) {
      setBlobImages([]);
      setFetchError(false);
      setIsLoading(false);
      return;
    }

    const cached = getCachedEntry(cacheKey, httpImages);
    if (cached) {
      // Same as StopImagesDialog: !proImages[showImages] ? fetch : hideLoader()
      setBlobImages(cached.blobImages);
      setFetchError(false);
      setIsLoading(false);
      return;
    }

    setBlobImages([]);
    setFetchError(false);
    setIsLoading(true);

    const imagesDataByKey = getMappedImageDataWithTimestamp(httpImages);
    let cancelled = false;

    downloadBlobDataByURLs(imagesDataByKey)
      .then(fetchedImages => {
        if (cancelled) return;
        const loaded = toBlobImageItems(fetchedImages);
        setCachedBlobImages(cacheKey, httpImages, loaded);
        setBlobImages(loaded);
        setIsLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setFetchError(true);
        setBlobImages([]);
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [cacheKey, images]);

  return { blobImages, fetchError, isLoading };
};
